import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import useLanguageChange from "../../hooks/useLanguageChange";

import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import {
    FILE_UPLOAD_LIMITATION,
    FROM_WHERE,
    MAX_ALLOWED_FILE_IN_SINGLE_TIME,
    MAX_IMAGE_COUNT_OF_INDEXED_DB,
    PICKED_FILES_FROM,
    SUB_SCREEN,
    UPLOADING_FILE_STATUS,
    UPLOAD_FILES_DATA,
} from "../../constants/UploadFilesConstants";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY, FIREBASE_LOG_VALUE } from "../../constants/FirebaseConstants";
import uploadFilesService from "../../services/uploadFilesService";
import { changeUploadFileSubScreen, updateImagesArray } from "../../reducers/uploadFile-reducer";
import CustomToast from "../UI/customToast";
import { LogFirebase } from "../../services/firestore";
import { isProfessionalEdition } from "../../shared/utility";
import { FONT_SIZE, SHAPE_THICKNESS } from "../../constants/Constants";
import { indexedDBService } from "../OfflineMode/indexedDBService";
import reduxStore from "../../configureEnvStore";

const URL = window.URL || window.webkitURL || window.mozURL || window.msURL;

const UploadFilesLoader = ({ selectedFiles }) => {
    const dispatch = useDispatch();

    const { imagesArray } = useSelector((state) => state.uploadFile);
    const { isOfflineModeActive } = useSelector((state) => state.offlineMode);

    const [processedImages, setProcessedImages] = useState({
        total: 0,
        processing: -1,
    });

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    const maxAllowedImages = useMemo(() => {
        return isProfessionalEdition() ? GLOBAL_MEETING_DATA.remainingFileCount : MAX_ALLOWED_FILE_IN_SINGLE_TIME;
    }, [imagesArray]);

    useEffect(() => {
        if (processedImages.processing > processedImages.total) {
            dispatch(changeUploadFileSubScreen(SUB_SCREEN.OFF_VIEW));
        }
    }, [processedImages]);

    useEffect(() => {
        if (selectedFiles?.length > 0) {
            /* When image array receive, start counter from 1 with process start and start processing first image of array */
            setProcessedImages({
                total: selectedFiles.length,
                processing: 1,
            });

            let id = 0;
            if (imagesArray.length > 0) {
                id = imagesArray?.slice(-1)[0]?.id + 1;
            }
            /* Reset logs array on starting the process */
            UPLOAD_FILES_DATA.logFileArray = [];
            processImage(0, id);
        }
    }, [selectedFiles]);

    useEffect(() => {
        if (imagesArray.length > 0 && processedImages.processing >= 0) {
            const id = imagesArray?.slice(-1)[0]?.id + 1;
            increaseIndex(id);
        }
    }, [imagesArray]);

    const updateArrayOfImages = (newImageObject) => {
        dispatch(updateImagesArray([...imagesArray, newImageObject]));
    };

    const updateArrayOfLogFiles = (newLogFileObject) => {
        UPLOAD_FILES_DATA.logFileArray.push(newLogFileObject);
    };

    const processImage = async (index, id) => {
        if (index >= selectedFiles.length) {
            return;
        }

        if (isOfflineModeActive) {
            const storedImages = await indexedDBService.getAllImages();
            LogFirebase("OfflineMode", "indexed_DB_store_image_count_selectedFiles_processImage", { count: storedImages?.length });
            // Count how many images are in MARKUP status
            const markupImageCount = imagesArray.filter((img) => img.uploadingStatus === UPLOADING_FILE_STATUS.MARKUP).length;
            const totalImageCount = markupImageCount + storedImages.length;
            if (totalImageCount >= MAX_IMAGE_COUNT_OF_INDEXED_DB) {
                const limitExceedMsg = localisedValues?.you_have_selected_the_maximum_number_of_files_allowed;
                CustomToast.info(limitExceedMsg);
                dispatch(changeUploadFileSubScreen(SUB_SCREEN.OFF_VIEW));
                return;
            }
        }

        if (imagesArray.filter((img) => img.uploadingStatus === UPLOADING_FILE_STATUS.MARKUP).length >= maxAllowedImages) {
            let limitExceedMsg = localisedValues?.max_file_count?.replace("<fileCount>", maxAllowedImages);
            if (isProfessionalEdition()) {
                limitExceedMsg = localisedValues?.you_have_selected_the_maximum_number_of_files_allowed;
                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.SelectImage, FIREBASE_LOG_VALUE.MaxUploadLimitReached);
            }
            CustomToast.info(limitExceedMsg);

            dispatch(changeUploadFileSubScreen(SUB_SCREEN.OFF_VIEW));
            return;
        }

        const file = selectedFiles[index];
        if (UPLOAD_FILES_DATA.pickedFilesFrom === PICKED_FILES_FROM.FROM_CAMERA && !PublicVariables.isMobile) {
            setTimeout(() => {
                //Use this timeout for making process cycle in proper queue, This is case only when we captured image from camera & adding those images in imagesArray
                const pushImageDataInArrayParameters = {
                    imageIndex: id,
                    imageDataSrc: file.imageData,
                    imageSize: file.imageSize,
                    imageExtension: file.extension,
                    imageLatVal: file.lat,
                    imageLongVal: file.long,
                    imageName: file.imageName,
                    fromWhere: FROM_WHERE.CAMERA,
                };
                pushImageDataInArray(pushImageDataInArrayParameters);
            }, 50);
        } else {
            let img = new Image();
            img.fileExt = file.name.substr(file.name.lastIndexOf(".") + 1);
            img.fileName = file.name;
            img.fileSize = file.size;
            img.type = file.type;
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const calculatedSize = uploadFilesService.transformImageSize(
                    img.width,
                    img.height,
                    FILE_UPLOAD_LIMITATION.IMAGE_MAX_ALLOWED_WIDTH_OR_HEIGHT
                );
                const loadedImage = img;
                resizeImage(loadedImage, calculatedSize, id, file);
            };
        }
    };

    const increaseIndex = (id) => {
        setProcessedImages((prevState) => {
            processImage(prevState.processing, id);
            return {
                total: prevState.total,
                processing: prevState.processing + 1,
            };
        });
    };

    const resizeImage = async (img, calculatedSize, id, file) => {
        const mainCanvas = document.createElement("canvas");
        mainCanvas.width = calculatedSize.w;
        mainCanvas.height = calculatedSize.h;
        const ctx = mainCanvas.getContext("2d");
        ctx.drawImage(img, 0, 0, mainCanvas.width, mainCanvas.height);
        const imageIndex = id; //getVal;//fetchIndex();

        /* Show lat-long value on image if image is captured from camera */
        let latVal = null;
        let longVal = null;
        const exifData = await uploadFilesService.getImageExifData(file);
        if (exifData) {
            //set latitude-longitude if available for image
            latVal = exifData.latitude;
            longVal = exifData.longitude;
        }
        /* update lat-long here if image is captured from camera */

        const pushImageDataInArrayParameters = {
            imageIndex: imageIndex,
            imageDataSrc: mainCanvas.toDataURL(img.type),
            imageSize: img.fileSize,
            imageExtension: img.fileExt,
            imageLatVal: latVal,
            imageLongVal: longVal,
            imageName: img.fileName,
            fromWhere: FROM_WHERE.GALLERY,
        };
        if (UPLOAD_FILES_DATA.pickedFilesFrom === PICKED_FILES_FROM.FROM_CAMERA && PublicVariables.isMobile && isOfflineModeActive) {
            pushImageDataInArrayParameters.imageLatVal = CALL_DATA.Latitude;
            pushImageDataInArrayParameters.imageLongVal = CALL_DATA.Longitude;
        }
        pushImageDataInArray(pushImageDataInArrayParameters);
    };

    /* common function for make markup image array with limit */
    const pushImageDataInArray = ({ imageIndex, imageDataSrc, imageSize, imageExtension, imageLatVal, imageLongVal, imageName, fromWhere }) => {
        updateThumbnail(imageDataSrc, (thumbnailData, dimension) => {
            const isOfflineModeActive = reduxStore.getState().offlineMode.isOfflineModeActive;
            const newImageObject = {
                id: isOfflineModeActive ? imageIndex + Date.now() : imageIndex,
                size: imageSize,
                name: imageName,
                type: fromWhere,
                src: imageDataSrc,
                updatedSrc: imageDataSrc,
                thumbnail: thumbnailData,
                compressedImage: null,
                shapeList: [],
                undoList: [],
                caption: "",
                anncolor: PublicVariables.agentDefaultColor,
                anncolorIndex: 0,
                extension: imageExtension,
                lat: imageLatVal,
                long: imageLongVal,
                uploadingStatus: UPLOADING_FILE_STATUS.MARKUP,
                selectedFontSize: FONT_SIZE[7], // 30px,
                selectedThickness: SHAPE_THICKNESS[5], // 10px,
                isShadowOrStorke: true,
            };
            updateArrayOfImages(newImageObject);

            const newLogFileObj = {
                size: uploadFilesService.formatBytes(imageSize),
                name: imageName,
                // from_type: fromWhere,
                extension: imageExtension,
                resolution: dimension,
            };

            updateArrayOfLogFiles(newLogFileObj);
        });
    };

    /* Create thumbnail by keeping 80 px as max width or height */
    const updateThumbnail = (imageDataSrc, callback) => {
        const img = new Image();
        img.onload = () => {
            let w = img.width;
            let h = img.height;
            const dimention = w + "*" + h;
            const ratio = w / h;
            if (ratio > 1) {
                img.width = 80;
                img.height = img.width / ratio;
            } else {
                img.height = 80;
                img.width = img.height * ratio;
            }

            callback(img.src, dimention);
        };
        img.src = imageDataSrc;
    };

    return (
        <div className='upload-file-loader' data-testid='uploadFileLoader'>
            <div className='upload-file-loader-in'>
                <div className='upload-file-loader-box'>
                    <div className='round-loader default-loader'></div>
                    <div className='upload-file-loader-text'>
                        {localisedValues.loading_image_counter
                            ?.replace(
                                "<counter>",
                                processedImages.processing > processedImages.total ? processedImages.total : processedImages.processing
                            )
                            .replace("<totalCount>", processedImages.total)}
                    </div>
                </div>
            </div>
        </div>
    );
};

UploadFilesLoader.propTypes = {
    selectedFiles: PropTypes.array,
};

export default UploadFilesLoader;
